import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Stack } from "@mantine/core";
import { useAuthStore } from "@/lib/auth";
import { AreaHeader } from "@/components/area-header";
import { CalendarFeedsCard } from "@/components/calendar-feeds-card";
import { ApprovalSettingsCard } from "@/components/booking-approvals/approval-settings-card";
import { BillingDetailsCard } from "@/components/settings/billing-details-card";
import { OpeningHoursEditor } from "@/components/settings/opening-hours-editor";
import { SlackSettingsSection } from "@/components/settings/slack-settings-section";
import { LandlordNotes } from "@/components/settings/landlord-notes";
import { DoorbellChimeCard } from "@/components/doorbell/doorbell-chime-card";
import { DoorbellCallSettingsCard } from "@/components/doorbell/call-settings-card";
import { useApprovalSettings } from "@/hooks/use-booking-approvals";
import { resolveSettingsArea, type SettingsAreaKey } from "@/lib/settings-areas";

/** What each area shows: the same cards the single Settings page stacked, unchanged. */
function AreaContent({ area }: { area: SettingsAreaKey }) {
  const navigate = useNavigate();
  switch (area) {
    case "slack":
      return <SlackSettingsSection />;
    case "opening-hours":
      return <OpeningHoursEditor />;
    case "billing":
      // A plan change parked for missing billing details resumes on the Network page once saved.
      return <BillingDetailsCard onResumePlanChange={() => navigate("/network")} />;
    case "booking-approval":
      return <ApprovalSettingsCard />;
    case "doorbell":
      return (
        <>
          {/* The chime: only sirens in rooms the company uses alone; nothing without one. */}
          <DoorbellChimeCard />
          {/* Live visitor calls from the reception tablet. */}
          <DoorbellCallSettingsCard />
        </>
      );
    case "calendar-feeds":
      return <CalendarFeedsCard />;
    case "notes":
      return <LandlordNotes />;
  }
}

/** One Settings area at `/settings/:area`. An unknown area, or one this member
 *  may not use (company-admin only, booking approval switched off), goes back
 *  to the Settings landing page. */
export default function SettingsAreaPage() {
  const { area: key } = useParams<{ area: string }>();
  const user = useAuthStore((s) => s.user);
  const { data: approval } = useApprovalSettings();
  const area = resolveSettingsArea(key, user, { approvalEnabled: approval?.enabled });

  if (!area) return <Navigate to="/settings" replace />;

  return (
    <Stack p="md" gap="md">
      <AreaHeader
        icon={area.icon}
        title={area.title}
        description={area.description}
        backTo="/settings"
        backLabel="All settings"
      />
      <AreaContent area={area.key} />
    </Stack>
  );
}
